/**
 * DataWithRoles
 *
 * Generic stamp that adds a per-resource `$roles` array to any base data shape.
 *
 * Stamped only on endpoints/views that opt in to frontend role-gating; not
 * added to every payload. Frontend gates via
 * `data.$roles.includes('event:owner')`-style membership checks.
 *
 * The role parameter `R` is constrained to `string` so call sites stamp a
 * concrete role union (e.g. `EventRole`, `UserRole`) rather than the generic
 * `string` type.
 */

import { z } from "zod";

export type DataWithRoles<T, R extends string> = T & {
  readonly $roles: readonly R[];
};

export function dataWithRolesSchema<
  TBase extends z.ZodType,
  TRole extends z.ZodType<string>,
>(base: TBase, role: TRole) {
  return z.intersection(
    base,
    z.object({
      $roles: z
        .array(role)
        .readonly()
        .refine((roles) => new Set(roles).size === roles.length, {
          message: "$roles must not contain duplicates",
        }),
    }),
  );
}
